import { useState, useCallback, useEffect, useRef } from "react";
import { Box, Alert, AlertIcon } from "@chakra-ui/react";
import { useLocation } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { startCreateTiliaBankAccount, finishCreateTiliaBankAccount } from "api/payments/index.js";
import { useSessionJWT, useSessionCtx, useAnalytics } from "utils";
import FormComponent from "./FormComponent";
import { FullPageSpinner } from "components/Spinner";
import SuccessScreen from "components/Success/SuccessScreen";
import { parseFinishCreateErrorForFieldLevelCodes } from "./utils";
import { logError } from "api/accounts-customer";

// France iban that will pass validation: FR2030003000401658975866Q64
// German iban that will pass validation: DE89370400440532013000

const BankAccountForm = ({ onSuccess, countryISO, currency }) => {
  const analytics = useAnalytics();
  const viewEventFired = useRef(false);
  const { formatMessage } = useIntl();
  let selectedCountryISO = countryISO;
  let selectedCurrency = currency;

  // not sure if this is needed.....
  // we will attempt to grab country ISO/currency from location hook instead
  // this is only possible if IbanForm is wrapped in React Router DOM
  // which it doesn't need to be.
  try {
    const location = useLocation();
    selectedCountryISO = location.state?.countryISO || countryISO;
    selectedCurrency = location.state?.currency || currency;
  } catch {} // eslint-disable-line no-empty

  const { sessionJWT } = useSessionJWT();
  const { sessionId } = useSessionCtx();
  const ctx = {
    ...sessionJWT,
    sessionId,
  };
  const [startCreateSuccessPayload, setStartCreateSuccessPayload] = useState(null);
  const [isLoadingStartCreate, setIsLoadingStartCreate] = useState(false);
  const [startCreateError, setStartCreateError] = useState(null);

  const [finishCreateSuccessPayload, setFinishCreateSuccessPayload] = useState(null);
  const [isLoadingFinishCreate, setIsLoadingFinishCreate] = useState(false);
  const [finishCreateError, setFinishCreateError] = useState(null);
  const [apiFieldLevelErrorCodes, setApiFieldLevelErrorCodes] = useState(null);

  const [renderSuccess, setRenderSuccess] = useState(false);

  useEffect(() => {
    // useEffect for tracking page view analytic
    if (!viewEventFired.current) {
      analytics.page("Banking", "AddManualBankAccountForm_Viewed", { countryISO });
      viewEventFired.current = true;
    }
  }, [analytics, viewEventFired, countryISO]);

  const startCreateBankAccount = useCallback(async (countryCode, currency) => {
    setStartCreateSuccessPayload(null);
    setIsLoadingStartCreate(true);
    setStartCreateError(null);

    try {
      const response = await startCreateTiliaBankAccount(ctx, countryCode, currency);
      setStartCreateSuccessPayload(response.payload.input_fields);
    } catch (err) {
      logError(sessionJWT, { message: err.message, flowLoc: "payout.startCreateBankAccount", data: err.data });
      setStartCreateError(err);
    } finally {
      setIsLoadingStartCreate(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // lint will complain about ctx not being included, but in this case initial render has the final value

  const finishCreateBankAccount = useCallback(
    async (data) => {
      setFinishCreateSuccessPayload(null);
      setIsLoadingFinishCreate(true);
      setFinishCreateError(null);
      setApiFieldLevelErrorCodes(null);

      let isSuccessful = false; // for analytics tracking
      try {
        const response = await finishCreateTiliaBankAccount(ctx, data);
        isSuccessful = true;
        setFinishCreateSuccessPayload(response.payload);
      } catch (err) {
        const fieldLevelErrCodes = parseFinishCreateErrorForFieldLevelCodes(err, formatMessage);
        if (fieldLevelErrCodes) {
          setApiFieldLevelErrorCodes(fieldLevelErrCodes);
        }
        logError(sessionJWT, { message: err.message, flowLoc: "payout.finishCreateBankAccount",  data: err.data });
        setFinishCreateError(err);
      } finally {
        setIsLoadingFinishCreate(false);
        analytics.track("AddManualBankForm_Submit", { countryISO, isSuccessful });
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [formatMessage],
  ); // lint will complain about ctx not being included, but in this case initial render has the final value

  useEffect(() => {
    startCreateBankAccount(selectedCountryISO, selectedCurrency);
  }, [startCreateBankAccount, selectedCountryISO, selectedCurrency]);

  useEffect(() => {
    if (finishCreateSuccessPayload) {
      setRenderSuccess(true);
    }
  }, [finishCreateSuccessPayload]);

  const handlePostSuccessRender = () => {
    setRenderSuccess(false);
    onSuccess && onSuccess(finishCreateSuccessPayload.bank_account_id);
  };

  const handleSubmit = useCallback(
    (formData) => {
      finishCreateBankAccount(formData);
    },
    [finishCreateBankAccount],
  );

  let content = null;

  if (isLoadingStartCreate) {
    // this is just for start-create, the spinner for finish-create is in the submit button
    content = <FullPageSpinner data-testid="bank-account-form-full-spinner" />;
  } else if (startCreateError || (finishCreateError && !apiFieldLevelErrorCodes)) {
    content = (
      <Alert status="error">
        <AlertIcon />
        <FormattedMessage
          defaultMessage="Apologies, there was an error. Please try again later."
          description="Error message"
        />
      </Alert>
    );
  } else if (renderSuccess) {
    content = <SuccessScreen onUnmount={handlePostSuccessRender} />;
  } else if (startCreateSuccessPayload) {
    content = (
      <FormComponent
        onSubmit={handleSubmit}
        requiredFields={startCreateSuccessPayload}
        countryCode={countryISO}
        isLoading={isLoadingFinishCreate}
        apiFieldLevelErrorCodes={apiFieldLevelErrorCodes}
      />
    );
  }

  return (
    <Box px="7" py="6">
      {content}
    </Box>
  );
};

export default BankAccountForm;
